import React, {FunctionComponent, ReactNode, useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import {ProgressBar} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import './Tests.scss';
import {Breadcrumbs, Button, ErrorMessage, Loader, Page, PageHeader} from 'components';
import {ErrorType, LoadState, RunGroupID, RunStatus, TestStatus} from 'enums';
import {RunGroup} from './RunGroup';
import {TestsModel, TestsModelFactory} from 'services';
import {formatDateShort, formatTime} from 'services/Utils';
import {TestEvents} from './TestEvents';

export const Tests: FunctionComponent<{}> = (props) => {
    const {t, i18n} = useTranslation();
    const urlParams = useParams<{ id: string }>();
    const deviceId = parseInt(urlParams.id, 10);

    // ----------- Required data for showing the page -----------
    const modelRef = useRef<TestsModel>();
    if (!modelRef.current) {
        // Initialize and tell our model to start loading.
        // By doing this here using a ref instead of in a useEffect, we save a rerender
        // because we're starting in the loading state.
        modelRef.current = TestsModelFactory.create(deviceId);
        modelRef.current.load();
    }
    const model = modelRef.current;
    // Read our state from the model using hooks. This way, we rerender if anything changes.
    const [modelState, setModelState] = model.useState();
    const {loadState, loadError, runStatus, runError, sseStatus, device, runGroups, events, isWaitingOnReboot, runDate} = modelState;

    // ----------- Computed properties -----------
    // We can only change selection if everything is loaded and the tests aren't running.
    const canChangeSelection = (loadState === LoadState.Success && runStatus === RunStatus.Initial);
    // The select all checkbox in the header is selected if every group is selected.
    const isSelectAllChecked = runGroups.every(runGroup => runGroup.selected);
    const selectedRunGroupIds = runGroups
        .filter(runGroup => runGroup.selected)
        .map(runGroup => runGroup.id);

    // Compute rollup counts. Need to get an array of all selected tests to get the total.
    const selectedTests = runGroups   
        .filter(runGroup => runGroup.selected)
        .reduce((acc, runGroup) => {
            return acc.concat(runGroup.tests);
        }, []);
    const countTotal = selectedTests.length;

    const countPass = selectedTests.filter(t => t.status === TestStatus.Passed).length;
    const countFail = selectedTests.filter(t => t.status === TestStatus.Failed).length;
    const countCanceled = selectedTests.filter(t => t.status === TestStatus.Canceled).length;
    const countInProgress = selectedTests.filter(t => t.status === TestStatus.InProgress).length;

    const runTests = () => {
        if (selectedRunGroupIds.length === 0) return;        
        model.startRun(selectedRunGroupIds);
    }

    const cancelTests = () => {
        model.stopRun();
    }

    const toggleSelectGroup = (runGroupId: RunGroupID) => {
        if (!canChangeSelection) return;

        for (let i = 0; i < runGroups.length; i++) {
            const g = runGroups[i];
            if (g.id === runGroupId) {
                // Flip the "selected" prop. We're creating a new runGroup object so react detects the change.
                runGroups[i] = {
                    ...g,
                    selected: !g.selected,
                }
                break;
            }
        }

        setModelState({
            ...modelState,
            // Create new array so react detects the change.
            runGroups: [...runGroups],
        })
    }

    const toggleSelectAll = () => {
        // Can only change selection if we're not running.
        if (!canChangeSelection) return;

        const desiredSelection = !isSelectAllChecked;
        // Change each group to the desired selection
        for (let i = 0; i < runGroups.length; i++) {
            const g = runGroups[i];

            if (g.selected !== desiredSelection) {
                runGroups[i] = {
                    ...g,
                    selected: desiredSelection,
                }
            }
        }

        setModelState({
            ...modelState,
            runGroups: [...runGroups],
        })
    }

    const breadcrumbs = (
        <Breadcrumbs crumbs={[
            {
                label: t('devicesList.title'),
                url: '/',
            },
        ]} />
    );

    let buttons: ReactNode = undefined;
    if (loadState === LoadState.Success) {
        switch (runStatus) {
            case RunStatus.Initial:
                buttons = (<>
                    <Button onClick={runTests} disabled={selectedRunGroupIds.length === 0} theme="red">
                        {t('tests.runTestsButton')}
                    </Button>
                </>);
                break;
            case RunStatus.Starting:
                buttons = (<>
                    <Button disabled={true} theme="red">
                        {t('tests.runTestsButton_starting')}
                    </Button>
                </>);
                break;
            case RunStatus.Running:
                buttons = (<>
                    <Button onClick={cancelTests} theme="red">
                        {t('tests.cancelTestsButton')}
                    </Button>
                    <Button disabled={true} theme="red">
                        {t('tests.runTestsButton_running')}
                    </Button>
                </>);
                break;
            case RunStatus.Canceling:
                buttons = (<>
                    <Button disabled={true} theme="red">
                        {t('tests.cancelTestsButton_canceling')}
                    </Button>
                    <Button disabled={true} theme="red">
                        {t('tests.runTestsButton_running')}
                    </Button>
                </>);
                break;
        }
    }

    let content: ReactNode = undefined;
    switch (loadState) {
        case LoadState.Initial:
        case LoadState.Loading:
            content = <Loader />;
            break;
        case LoadState.Success: {
            // Each string goes in a div as well as a title, so precompute translations to avoid doing them twice.
            const t_firmware = t('devicesList.item.firmware');
            const t_serial = t('devicesList.item.serial');
            const t_location = t('devicesList.item.location');
            const t_firmwareTitle = t_firmware + ' ' + device.firmware.firmware_version;
            const t_serialTitle = t_serial + ' ' + device.serial_number;
            const t_locationTitle = t_location + ' ' + device.location;
            const t_lastRunDate = formatDateShort(runDate);
            const t_lastRunTime = formatTime(runDate);
            const t_lastRun = runDate && t('tests.runInfo.lastRun', {runDate: t_lastRunDate, runTime: t_lastRunTime});

            content = (<>
                {runError && (<>
                    <ErrorMessage type={ErrorType.Generic} message={runError} />
                    <hr />
                </>)}
                <div className="tests_infoSection">
                    <div className="tests_infoDevice">
                        <div className="testsDevice_wrapper">
                            <div className="testsDevice">
                                <div className="testsDevice_iconCol">
                                    <div className="testsDevice_iconCircle">
                                        <FontAwesomeIcon icon="hdd" className="testsDevice_icon" />
                                    </div>
                                </div>
                                <div className="testsDevice_makeModelCol">
                                    <div className="testsDevice_name ellipsis" title={device.model.name}>
                                        {device.model.name}
                                    </div>
                                    <div className="testsDevice_manufacturer ellipsis" title={device.manufacturer.name}>
                                        {device.manufacturer.name}
                                    </div>
                                </div>
                                <div className="testsDevice_detailsCol">
                                    <div className="testsDevice_detailsLeft">
                                        <div className="ellipsis" title={t_firmwareTitle}>
                                            {t_firmware}
                                        </div>
                                        <div className="ellipsis" title={t_serialTitle}>
                                            {t_serial}
                                        </div>
                                        <div className="ellipsis" title={t_locationTitle}>
                                            {t_location}
                                        </div>
                                    </div>
                                    <div className="testsDevice_detailsRight">
                                        <div className="ellipsis" title={t_firmwareTitle}>
                                            {device.firmware.firmware_version}
                                        </div>
                                        <div className="ellipsis" title={t_serialTitle}>
                                            {device.serial_number}
                                        </div>
                                        <div className="ellipsis" title={t_locationTitle}>
                                            {device.location}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="tests_infoCounts">
                        <div className="tests_infoCountsGrid">
                            <span className="tests_infoCountsLabel" title={t_lastRun}>
                                {t('tests.runInfo.lastRunLabel')}
                            </span>
                            <span className="tests_infoCountsValue" title={t_lastRun}>
                                {t_lastRun}
                            </span>
                            <span className="tests_infoCountsLabel">
                                {t('tests.runInfo.resultsLabel')}
                            </span>
                            <span className="tests_infoCountsValue">
                                {t('tests.runInfo.results', {countPass: countPass, countTotal: countTotal})}
                            </span>
                        </div>
                        <ProgressBar>
                            <ProgressBar striped variant="success" now={100*countPass/countTotal} key={1} />
                            <ProgressBar striped variant="danger" now={100*countFail/countTotal} key={2} />
                            <ProgressBar striped variant="warning" now={100*countCanceled/countTotal} key={3} />
                            <ProgressBar striped variant="inprogress" now={100*countInProgress/countTotal} key={4} />
                        </ProgressBar>
                    </div>
                </div>
                <p className="tests_instructions">
                    {t('tests.instructions')}
                </p>
                <p className="tests_instructions">
                    {t('tests.instructionsSDKNote')}
                </p>
                <div className="tests_headers">
                    <label className="tests_headerSelect">
                        <input
                            type="checkbox"
                            checked={isSelectAllChecked}
                            onChange={toggleSelectAll}
                            disabled={!canChangeSelection}
                        />
                        <span className="tests_headerSelectLabel">
                            {t('tests.headers.selectAll')}
                        </span>
                    </label>
                </div>
                <ul className="tests_list">
                    {runGroups.map(g => (
                        <RunGroup
                            canChangeSelection={canChangeSelection}
                            key={g.id}
                            name={g.name}
                            onSelect={() => toggleSelectGroup(g.id)}
                            selected={g.selected}
                            tests={g.tests}
                            deviceId={deviceId}
                        />
                    ))}
                </ul>
            </>);
            break;
        }
        case LoadState.Error:
            content = <ErrorMessage type={ErrorType.Generic} message={loadError} />
            break;
    }

    return (
        <div className="testsPage_wrapper">
            <div className="testsPage_left">
                <Page>
                    <PageHeader
                        top={breadcrumbs}
                        title={t('tests.title')}
                        right={(<div className="tests_buttons">
                            {buttons}
                        </div>)}
                    />
                    {content}
                </Page>
            </div>
            <div className="testsPage_right">
                <TestEvents sseStatus={sseStatus} events={events} isWaitingOnReboot={isWaitingOnReboot}/>
            </div>
        </div>
    );
}